import { fireEvent, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import type { GridRenderCellParams, GridColDef } from "@mui/x-data-grid";
import {
  applyColumnDisplay,
  readColumnDisplay,
} from "./viewGrid/columnDisplay";
import CanvasRowList, {
  readRowListConfig,
} from "./canvasBuilder/elements/CanvasRowList";
import { CanvasRenderer } from "./canvasBuilder/CanvasRenderer";
import { formatRecordLabelTemplate } from "./recordLabelTemplate";

const row = {
  id: "r1",
  first_name: "Alex",
  last_name: "Example",
  name: "Stored name",
};

it("uses only returned scalar columns, retaining zero and false without evaluating expressions", () => {
  expect(
    formatRecordLabelTemplate("{first_name} {missing} {last_name}", row),
  ).toBe("Alex Example");
  expect(
    formatRecordLabelTemplate(
      "{zero}/{flag}/{object}/{inherited}",
      Object.assign(Object.create({ inherited: "hidden" }), {
        zero: 0,
        flag: false,
        object: { secret: "not displayed" },
      }),
    ),
  ).toBe("0/false//");
  expect(formatRecordLabelTemplate("{first_name.toUpperCase()}", row)).toBe("");
  expect(
    formatRecordLabelTemplate("{person.first_name}", {
      "person.first_name": "Alex",
    }),
  ).toBe("Alex");
});

it("renders authored markup as text, never HTML", () => {
  render(
    <CanvasRowList
      rows={[row]}
      config={{
        primaryField: "name",
        recordLabelTemplate: "<b>{first_name}</b>",
      }}
      onRowSelect={jest.fn()}
    />,
  );
  expect(screen.getByText("<b>Alex</b>")).toBeInTheDocument();
});

it("formats a grid cell from returned row values while retaining editing and sort/search identity", () => {
  const editor = jest.fn();
  const original = {
    field: "first_name",
    editable: true,
    sortable: true,
    renderEditCell: editor,
  } as GridColDef;
  const column = applyColumnDisplay(
    [original],
    readColumnDisplay({
      columnRecordLabelTemplates: { first_name: "{first_name} {last_name}" },
    }),
  )[0];
  render(column.renderCell!({ value: "Alex", row } as GridRenderCellParams));
  expect(screen.getByText("Alex Example")).toBeInTheDocument();
  expect(column.field).toBe("first_name");
  expect(column.renderEditCell).toBe(editor);
  expect(column.editable).toBe(true);
  expect(column.sortable).toBe(true);
  expect(column.valueGetter).toBeUndefined();
});

it("formats list labels without changing the selected row", () => {
  const select = jest.fn();
  const config = readRowListConfig({
    display: "list",
    list_config: {
      primary_field: "name",
      record_label_template: "{first_name} {last_name}",
    },
  })!;
  render(<CanvasRowList rows={[row]} config={config} onRowSelect={select} />);
  fireEvent.click(screen.getByText("Alex Example"));
  expect(select).toHaveBeenCalledWith("r1", row);
});

it("formats a FIELD display while the original editable control retains its value", () => {
  const change = jest.fn();
  render(
    <CanvasRenderer
      mode="runtime"
      config={{ columns: 12, rowHeight: 60, gap: 8, minRows: 2 }}
      fieldState={{
        first_name: { value: "Alex", onChange: change },
        last_name: { value: "Example" },
      }}
      elements={[
        {
          id: "name",
          type: "field",
          variant: "text",
          props: {
            label: "Composed name",
            displayMode: "text",
            record_label_template: "{first_name} {last_name}",
          },
          data_binding: { data_source_id: "main", field_path: "first_name" },
          gridColumn: 1,
          gridRow: 1,
          columnSpan: 6,
          rowSpan: 1,
        },
        {
          id: "edit",
          type: "field",
          variant: "text",
          props: {
            label: "First name",
            record_label_template: "{first_name} {last_name}",
          },
          data_binding: { data_source_id: "main", field_path: "first_name" },
          gridColumn: 1,
          gridRow: 2,
          columnSpan: 6,
          rowSpan: 1,
        },
      ]}
    />,
  );
  expect(screen.getByText("Alex Example")).toBeInTheDocument();
  expect(screen.getByRole("textbox")).toHaveValue("Alex");
  fireEvent.change(screen.getByRole("textbox"), { target: { value: "Sam" } });
  expect(change).toHaveBeenCalledWith("Sam");
});
